import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import FormField from '@adsk/alloy-react-form-field';
import SelectBox from '@adsk/alloy-select-box';
import {
  COMPONENT_VARIANTS,
  removeExtraBlankSpaces,
} from '@adsk/acs-admin-ui-components-helpers';
import { useStore } from '../../../StoreProvider';
import CompanyFieldFooter from '../../../CompanyFieldFooter';
import Label from '../../../Label';
import { fieldStyle } from '../../styles';
import { ACTIONS } from '../../state/actions';

const CompanyField = ({
  canCreateCompanies,
  companyCreateError,
  companies,
  onChangeCompanyInput,
  variant,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { state, dispatch } = useStore();
  const prefixDataTestId =
    variant === COMPONENT_VARIANTS.PROJECT
      ? 'InviteProjectMemberModal'
      : 'InviteAccountMemberModal';

  const handleCompanyInputChange = (value) => {
    dispatch({
      type: ACTIONS.SET_COMPANY_SEARCH_INPUT,
      payload: value,
    });
  };

  const handleCompanyChange = (value) => {
    dispatch({
      type: ACTIONS.SET_COMPANY,
      payload: value,
    });
  };

  const openCompanySelectBox = (value) => {
    dispatch({
      type: ACTIONS.OPEN_COMPANY_SELECT_BOX,
      payload: value,
    });
  };

  const handleAddCompanyClick = () => {
    dispatch({
      type: ACTIONS.SET_COMPANY,
      payload: {
        name: removeExtraBlankSpaces(state.companySearchInput),
        id: state.companySearchInput,
      },
    });
    dispatch({ type: ACTIONS.SET_IS_NEW_COMPANY });
    openCompanySelectBox(false);
  };

  const latestOnChangeCompanyInput = useRef(onChangeCompanyInput);

  useEffect(() => {
    latestOnChangeCompanyInput.current = onChangeCompanyInput;
  }, [onChangeCompanyInput]);

  useEffect(() => {
    const handler = setTimeout(() => {
      latestOnChangeCompanyInput.current(
        removeExtraBlankSpaces(state.companySearchInput)
      );
    }, 800);

    return () => clearTimeout(handler);
  }, [state.companySearchInput]);

  const label =
    variant !== COMPONENT_VARIANTS.PROJECT ? (
      T.translate('COMPANY_LABEL')
    ) : (
      <Label
        required
        text={T.translate('COMPANY_LABEL')}
        tooltipMessage={T.translate('COMPANY_LABEL_INFO')}
        size={13}
        labelStyle={{ paddingBottom: '0px' }}
      />
    );

  return (
    <FormField
      label={label}
      labelVariant={FormField.LABEL_VARIANTS.TOP}
      error={companyCreateError ? T.translate('ADD_COMPANY_ERROR') : ''}
      style={fieldStyle}
      data-testid={`${prefixDataTestId}_company_field`}
    >
      <SelectBox
        clearable={variant !== COMPONENT_VARIANTS.PROJECT}
        data-testid={`${prefixDataTestId}_company_selectBox`}
        footerRenderer={() => {
          return (
            <CompanyFieldFooter
              canCreateCompanies={canCreateCompanies}
              companies={companies}
              inputValue={state.companySearchInput}
              onCreateCompany={handleAddCompanyClick}
            />
          );
        }}
        ignoreCase
        invalid={companyCreateError}
        isOpen={state.isSelectBoxOpen}
        labelKey="name"
        subtextKey={variant === COMPONENT_VARIANTS.PROJECT ? 'description' : ''}
        maxMenuHeight={200}
        onChange={handleCompanyChange}
        onClose={() => openCompanySelectBox(false)}
        onInputChange={handleCompanyInputChange}
        onOpen={() => openCompanySelectBox(true)}
        options={companies}
        placeholder={T.translate('COMPANY_PLACEHOLDER')}
        searchable
        value={state.selectedCompany}
        valueKey="id"
        default
      />
    </FormField>
  );
};

CompanyField.propTypes = {
  canCreateCompanies: PropTypes.bool.isRequired,
  companyCreateError: PropTypes.bool.isRequired,
  companies: PropTypes.array,
  onChangeCompanyInput: PropTypes.func.isRequired,
  variant: PropTypes.string.isRequired,
};

export default CompanyField;
